/**
 * Microbiology Dynamic Template Engine & Clinical Rules Engine
 * Configures master-driven templates, specimen-aware defaults, structured microscopy,
 * culture conditions, resistance flagging heuristics, and workflow next-action resolution.
 */

// 1. Investigation & Specimen Template Configurations
export const INVESTIGATION_TEMPLATES = {
  URINE: {
    name: 'Urine Culture & Susceptibility',
    specimenType: 'Urine',
    microscopyFields: ['pusCells', 'rbc', 'epithelialCells', 'bacteria', 'yeast', 'casts', 'crystals'],
    colonyCountUnit: 'CFU/mL',
    colonyCountPresets: [
      'No growth detected',
      '< 10^3 CFU/mL (Not Significant)',
      '10^3 – 10^4 CFU/mL (Doubtful Significance)',
      '10^4 – 10^5 CFU/mL (Moderate Count)',
      '> 10^5 CFU/mL (Significant Bacteriuria)',
      '> 10^5 CFU/mL (Pure Monomorphic Culture)'
    ],
    defaultMedia: ['CLED Agar', 'Blood Agar', 'MacConkey Agar'],
    significanceRules: [
      { minCount: 100000, text: 'Significant Pure Growth (Primary Pathogen)' },
      { minCount: 10000, text: 'Equivocal Count - Correlate Clinically' },
      { minCount: 0, text: 'Insignificant / Commensal' }
    ],
    quickTemplates: [
      {
        id: 'urine_normal',
        name: 'Normal Sterile Urine',
        pusCells: '1–5 / HPF',
        rbc: 'Nil',
        epithelialCells: '1–3 / HPF',
        gramStain: 'No microorganisms seen in uncentrifuged wet mount / Gram smear'
      },
      {
        id: 'urine_uti',
        name: 'Active Acute UTI',
        pusCells: '21–50 / HPF',
        rbc: '3–5 / HPF',
        epithelialCells: '1–3 / HPF',
        gramStain: 'Gram-negative bacilli seen (plentiful), pus cells present in sheets'
      },
      {
        id: 'urine_contam',
        name: 'Superficial Contamination',
        pusCells: '6–10 / HPF',
        rbc: 'Nil',
        epithelialCells: '>25 / HPF (Squamous contamination)',
        gramStain: 'Mixed bacterial flora seen with abundant squamous epithelial cells'
      }
    ]
  },
  BLOOD: {
    name: 'Blood Culture & Susceptibility',
    specimenType: 'Blood',
    microscopyFields: ['brothGramStain', 'smearMorphology', 'wbc'],
    colonyCountUnit: 'Bottles Positive',
    colonyCountPresets: [
      'Aerobic bottle positive at 14h 20m',
      'Anaerobic bottle positive at 22h 10m',
      'Both Aerobic & Anaerobic bottles positive',
      'No growth detected after 5 days incubation',
      'Extended incubation negative after 7 days'
    ],
    defaultMedia: ['Automated BACTEC / BacT/ALERT Broth', 'Blood Agar Subculture', 'Chocolate Agar'],
    significanceRules: [
      { minCount: 1, text: 'Critical Bloodstream Pathogen' }
    ],
    quickTemplates: [
      {
        id: 'blood_gnb',
        name: 'Positive Broth: Gram-Negative Bacilli',
        pusCells: 'Not Applicable',
        rbc: 'Not Applicable',
        epithelialCells: 'Nil',
        gramStain: 'Direct smear from positive blood bottle shows Gram-negative bacilli'
      },
      {
        id: 'blood_gpc_clusters',
        name: 'Positive Broth: GPC in Clusters (Staph)',
        pusCells: 'Not Applicable',
        rbc: 'Not Applicable',
        epithelialCells: 'Nil',
        gramStain: 'Direct smear from positive blood bottle shows Gram-positive cocci in irregular clusters'
      },
      {
        id: 'blood_gpc_chains',
        name: 'Positive Broth: GPC in Chains (Strep)',
        pusCells: 'Not Applicable',
        rbc: 'Not Applicable',
        epithelialCells: 'Nil',
        gramStain: 'Direct smear from positive blood bottle shows Gram-positive cocci in pairs and long chains'
      }
    ]
  },
  SPUTUM: {
    name: 'Sputum / BAL / Endotracheal Culture',
    specimenType: 'Sputum',
    microscopyFields: ['pusCells', 'epithelialCells', 'bartlettScore', 'gramStain', 'afbStain'],
    colonyCountUnit: 'Colony Density',
    colonyCountPresets: [
      'Heavy growth (+++) of predominant organism',
      'Moderate growth (++)',
      'Scanty growth (+)',
      'Normal Upper Respiratory Flora only',
      'No significant pathogen isolated'
    ],
    defaultMedia: ['Blood Agar', 'Chocolate Agar', 'MacConkey Agar'],
    significanceRules: [
      { minCount: 2, text: 'Significant Lower Respiratory Tract Pathogen' }
    ],
    quickTemplates: [
      {
        id: 'sputum_purulent',
        name: 'Representative Purulent Sputum (Good Quality)',
        pusCells: '>50 / HPF (Neutrophils > 25/LPF)',
        rbc: '1–2 / HPF',
        epithelialCells: '<5 / HPF (Squamous < 10/LPF)',
        gramStain: 'Gram-negative bacilli predominantly seen with abundant polymorphonuclear leukocytes (Bartlett Score +2)'
      },
      {
        id: 'sputum_saliva',
        name: 'Salivary Contamination (Unsatisfactory)',
        pusCells: '1–5 / HPF',
        rbc: 'Nil',
        epithelialCells: '>25 / HPF (Squamous > 25/LPF)',
        gramStain: 'Specimen predominantly saliva with mixed oral commensals. Suggest fresh recollection.'
      }
    ]
  },
  PUS_SWAB: {
    name: 'Pus / Wound Swab / Tissue Culture',
    specimenType: 'Pus / Swab',
    microscopyFields: ['pusCells', 'rbc', 'epithelialCells', 'gramStain', 'organismMorphology'],
    colonyCountUnit: 'Growth Degree',
    colonyCountPresets: [
      'Heavy pure growth (+++)',
      'Moderate growth (++)',
      'Scanty growth (+)',
      'Mixed skin flora isolated',
      'No growth after 48 hours aerobic incubation'
    ],
    defaultMedia: ['Blood Agar', 'MacConkey Agar', 'Thioglycollate Broth'],
    significanceRules: [
      { minCount: 1, text: 'Significant Pyogenic Pathogen' }
    ],
    quickTemplates: [
      {
        id: 'pus_heavy_gpc',
        name: 'Acute Pyogenic Abscess (Staph)',
        pusCells: '>50 / HPF (Sheets of pus cells)',
        rbc: '3–5 / HPF',
        epithelialCells: 'Nil',
        gramStain: 'Numerous Gram-positive cocci in clusters with heavy polymorphs'
      },
      {
        id: 'pus_pseudomonas',
        name: 'Wound Infection (Pseudomonas / GNB)',
        pusCells: '21–50 / HPF',
        rbc: '1–2 / HPF',
        epithelialCells: '1–3 / HPF',
        gramStain: 'Gram-negative bacilli seen with moderate pus cells and tissue debris'
      }
    ]
  },
  STOOL: {
    name: 'Stool Culture & Pathogen Screen',
    specimenType: 'Stool',
    microscopyFields: ['pusCells', 'rbc', 'hangingDropMotility', 'protozoanOva', 'gramStain'],
    colonyCountUnit: 'Colony Degree',
    colonyCountPresets: [
      'Heavy pure growth of non-lactose fermenting pathogen',
      'Normal Enteric Commensal Flora (E. coli, Klebsiella)',
      'No Salmonella, Shigella or Vibrio isolated',
      'No enteric pathogen isolated after selective enrichment'
    ],
    defaultMedia: ['XLD Agar', 'DCA Agar', 'MacConkey Agar', 'TCBS Agar', 'Selenite F Broth'],
    significanceRules: [
      { minCount: 1, text: 'Enteric Pathogen of Clinical Significance' }
    ],
    quickTemplates: [
      {
        id: 'stool_normal',
        name: 'Normal Faecal Flora',
        pusCells: '0–2 / HPF',
        rbc: 'Nil',
        epithelialCells: 'Few',
        gramStain: 'Normal faecal flora with Gram-negative bacilli and Gram-positive flora'
      },
      {
        id: 'stool_bacillary_dysentery',
        name: 'Bacillary Dysentery Pattern',
        pusCells: '>50 / HPF (Clumps of pus cells)',
        rbc: '21–50 / HPF (Microscopic hematochezia)',
        epithelialCells: 'Few',
        gramStain: 'Sheets of pus cells and erythrocytes with Gram-negative bacilli'
      }
    ]
  },
  BODY_FLUID: {
    name: 'Sterile Body Fluid / CSF / Synovial / Pleural Culture',
    specimenType: 'Body Fluid',
    microscopyFields: ['pusCells', 'rbc', 'lymphocytes', 'gramStain', 'indiaInk', 'wetMount'],
    colonyCountUnit: 'Growth Density',
    colonyCountPresets: [
      'Pure heavy growth from sterile site',
      'Moderate growth',
      'Scanty growth in enrichment broth only',
      'No growth detected after 72 hours incubation'
    ],
    defaultMedia: ['Chocolate Agar (5% CO2)', 'Blood Agar', 'Thioglycollate Broth', 'MacConkey Agar'],
    significanceRules: [
      { minCount: 1, text: 'Critically Significant Sterile Site Pathogen' }
    ],
    quickTemplates: [
      {
        id: 'csf_meningitis_gpc',
        name: 'Bacterial Meningitis (Pneumococcal / GPC Pairs)',
        pusCells: '>50 / HPF (Marked pleocytosis)',
        rbc: '1–2 / HPF',
        epithelialCells: 'Nil',
        gramStain: 'Flame-shaped Gram-positive diplococci seen with capsular halos and numerous polymorphs'
      },
      {
        id: 'sterile_fluid_sterile',
        name: 'Sterile Aspirate (No Organisms)',
        pusCells: 'Nil to 1–2 / HPF',
        rbc: 'Nil',
        epithelialCells: 'Nil',
        gramStain: 'No microorganisms seen on centrifuged smear deposit'
      }
    ]
  }
};

// 2. Structured Microscopy Observation Options
export const MICROSCOPY_OPTIONS = {
  gramReactions: [
    { value: 'Gram Positive', label: 'Gram Positive (+)', color: '#7c3aed' },
    { value: 'Gram Negative', label: 'Gram Negative (-)', color: '#e11d48' },
    { value: 'Mixed Gram Flora', label: 'Mixed Gram (+ / -)', color: '#d97706' },
    { value: 'Gram Variable', label: 'Gram Variable', color: '#64748b' },
    { value: 'Not Seen', label: 'No Organisms Seen', color: '#059669' }
  ],
  morphologies: [
    { value: 'Cocci', label: 'Cocci' },
    { value: 'Bacilli', label: 'Bacilli (Rods)' },
    { value: 'Coccobacilli', label: 'Coccobacilli' },
    { value: 'Diplococci', label: 'Diplococci' },
    { value: 'Yeast / Budding Cells', label: 'Yeast / Budding Cells' },
    { value: 'Fungal Hyphae / Elements', label: 'Fungal Hyphae' },
    { value: 'Pleomorphic Rods', label: 'Pleomorphic Rods' },
    { value: 'Curved Bacilli / Vibrio', label: 'Curved / Comma Bacilli' }
  ],
  arrangements: [
    { value: 'Clusters', label: 'In Clusters (Grape-like)' },
    { value: 'Chains', label: 'In Chains' },
    { value: 'Pairs', label: 'In Pairs (Diplococci)' },
    { value: 'Singles', label: 'Singles / Discrete' },
    { value: 'Chinese-letter / Palusades', label: 'Palisades / Cuneiform' },
    { value: 'Intracellular & Extracellular', label: 'Intracellular within Pus Cells' }
  ],
  quantities: [
    { value: 'Few / Occasional (1+)', label: 'Few (1+)' },
    { value: 'Moderate (2+)', label: 'Moderate (2+)' },
    { value: 'Many / Abundant (3+)', label: 'Many / Plentiful (3+)' },
    { value: 'Sheets / Innumerable (4+)', label: 'Heavy Sheets (4+)' }
  ],
  wbcRanges: [
    'Nil',
    '< 1 / HPF',
    '1–5 / HPF',
    '6–10 / HPF',
    '11–20 / HPF',
    '21–50 / HPF',
    '> 50 / HPF',
    'Pus cells present in sheets / Clumps'
  ],
  rbcRanges: [
    'Nil',
    '0–2 / HPF',
    '3–5 / HPF',
    '6–10 / HPF',
    '11–25 / HPF',
    '> 25 / HPF',
    'Gross Hematuria / Packed Field'
  ],
  epithelialRanges: [
    'Nil',
    '1–3 / HPF (Uncontaminated)',
    '4–8 / HPF',
    '10–20 / HPF',
    '> 25 / HPF (Superficial Contamination)',
    'Moderate Squamous Cells',
    'Abundant Squamous Cells'
  ],
  bacteriaRanges: [
    'Nil',
    'Few / Occasional',
    'Moderate',
    'Many / Plentiful',
    'Heavy / Overwhelming'
  ],
  castsCrystals: [
    'None Detected',
    'Hyaline Casts (1–2 / LPF)',
    'Granular Casts Seen',
    'WBC Casts (Indicating Pyelonephritis)',
    'Calcium Oxalate Crystals',
    'Triple Phosphate Crystals',
    'Uric Acid Crystals',
    'Amorphous Urates / Phosphates'
  ]
};

// 3. Culture Media & Environment Masters
export const CULTURE_MEDIA_OPTIONS = [
  'Blood Agar (5% Sheep Blood)',
  'MacConkey Agar (with Salt)',
  'MacConkey Agar (without Salt)',
  'CLED Agar (Cystine-Lactose-Electrolyte-Deficient)',
  'Chocolate Agar (Enriched with IsoVitaleX)',
  'Sabouraud Dextrose Agar (SDA)',
  'Nutrient Agar',
  'Thioglycollate Broth',
  'Brain Heart Infusion (BHI) Broth',
  'Mannitol Salt Agar (MSA)',
  'XLD Agar',
  'DCA Agar',
  'TCBS Vibrio Selective Agar',
  'Lowenstein-Jensen (LJ) Medium'
];

export const INCUBATION_ATMOSPHERES = [
  'Ambient Air (Aerobic 35°C–37°C)',
  '5% CO2 (Capnophilic Candle Jar / Incubator)',
  'Anaerobic System (GasPak / Anaerobic Jar)',
  'Microaerophilic (5% O2, 10% CO2, 85% N2)',
  'Fungal Incubator (28°C–30°C Ambient)'
];

export const IDENTIFICATION_METHODS = [
  'Automated VITEK-2 Compact System',
  'MALDI-TOF Mass Spectrometry (Bruker Biotyper)',
  'Standard Manual Biochemical Panel',
  'BD Phoenix Automated Identification',
  'MicroScan WalkAway System',
  'API 20E / API 20NE Strip Identification',
  'Direct Rapid Tests (Catalase, Oxidase, Slide Coagulase)',
  'Serological Latex Agglutination'
];

export const COLONY_MORPHOLOGY_PRESETS = [
  'Smooth pink lactose-fermenting colonies on MacConkey',
  'Pale non-lactose fermenting colonies on MacConkey',
  'Large mucoid lactose-fermenting colonies (Klebsiella type)',
  'Golden yellow colonies with complete Beta-hemolysis on Blood Agar',
  'Green metallic sheen on EMB / Pale translucent on CLED',
  'Swarming wave growth across entire plate (Proteus type)',
  'Dry, flat, spreading colonies with jagged edges',
  'Small translucent alpha-hemolytic colonies (Pneumo/Viridans)',
  'Creamy, convex, smooth colonies with distinct yeast aroma',
  'Blue-green colonies with fruity grape-like aroma (Pseudomonas type)',
  'White porcelain-like non-hemolytic colonies (Staph epidermidis type)'
];

// 4. Resistance Mechanism Screening Heuristics
export function evaluateResistanceAlerts(isolate, activeMechanisms = []) {
  if (!isolate || !isolate.astResults) return [];

  const alerts = [];
  const results = isolate.astResults;

  const findDrug = (codeOrName) => {
    const term = codeOrName.toLowerCase();
    return results.find(
      (r) =>
        (r.drugCode && r.drugCode.toLowerCase() === term) ||
        (r.drugName && r.drugName.toLowerCase().includes(term))
    );
  };

  const cro = findDrug('CRO') || findDrug('ceftriaxone');
  const caz = findDrug('CAZ') || findDrug('ceftazidime');
  const ctx = findDrug('CTX') || findDrug('cefotaxime');
  const mem = findDrug('MEM') || findDrug('meropenem');
  const ipm = findDrug('IPM') || findDrug('imipenem');
  const etp = findDrug('ETP') || findDrug('ertapenem');
  const oxa = findDrug('OXA') || findDrug('oxacillin');
  const fox = findDrug('FOX') || findDrug('cefoxitin');
  const van = findDrug('VAN') || findDrug('vancomycin');
  const ery = findDrug('ERY') || findDrug('erythromycin');
  const cli = findDrug('CLI') || findDrug('clindamycin');
  const col = findDrug('COL') || findDrug('colistin');

  // ESBL Detection
  const hasEsblIndicator =
    (cro && (cro.finalInterpretation === 'R' || (cro.measuredValue && cro.measuredValue <= 22))) ||
    (caz && (caz.finalInterpretation === 'R' || (caz.measuredValue && caz.measuredValue <= 19))) ||
    (ctx && (ctx.finalInterpretation === 'R' || (ctx.measuredValue && ctx.measuredValue <= 22)));

  if (hasEsblIndicator) {
    alerts.push({
      code: 'ESBL',
      name: 'Extended-Spectrum Beta-Lactamase (ESBL) Pattern',
      category: 'Infection Control Alert',
      severity: 'high',
      evidence: `Ceftriaxone / Ceftazidime / Cefotaxime non-susceptible (Measured: ${cro?.measuredValue || caz?.measuredValue || 'Resistant'}). Phenotypic ESBL confirmation warranted.`,
      recommendedAction: 'Confirm with Ceftazidime-Clavulanate synergy disk testing; review Carbapenem cascade.'
    });
  }

  // CRE / CPE Detection
  const hasCreIndicator =
    (mem && (mem.finalInterpretation === 'R' || (mem.measuredValue && mem.measuredValue <= 22))) ||
    (ipm && (ipm.finalInterpretation === 'R' || (ipm.measuredValue && ipm.measuredValue <= 22))) ||
    (etp && (etp.finalInterpretation === 'R' || (etp.measuredValue && etp.measuredValue <= 24)));

  if (hasCreIndicator) {
    alerts.push({
      code: 'CRE',
      name: 'Carbapenem-Resistant Enterobacterales (CRE/CPE) Alert',
      category: 'Critical Infection Control Alert',
      severity: 'critical',
      evidence: `Meropenem / Imipenem / Ertapenem non-susceptible (Measured: ${mem?.measuredValue || ipm?.measuredValue || etp?.measuredValue || 'Resistant'}).`,
      recommendedAction: 'Immediate notification to Infection Control Committee; perform mCIM / CarbeneX testing.'
    });
  }

  // MRSA Detection
  const hasMrsaIndicator =
    (fox && (fox.finalInterpretation === 'R' || (fox.measuredValue && fox.measuredValue <= 21))) ||
    (oxa && (oxa.finalInterpretation === 'R' || (oxa.measuredValue && oxa.measuredValue <= 17)));

  if (hasMrsaIndicator && isolate.organismName?.toLowerCase().includes('staph')) {
    alerts.push({
      code: 'MRSA',
      name: 'Methicillin-Resistant Staphylococcus aureus (MRSA)',
      category: 'Infection Control Alert',
      severity: 'high',
      evidence: `Cefoxitin screen positive (Zone <= 21mm). Indicates mecA/mecC mediated oxacillin resistance.`,
      recommendedAction: 'Report all beta-lactams (penicillins, cephalosporins, carbapenems) as resistant.'
    });
  }

  // VRE Detection
  const hasVreIndicator = van && (van.finalInterpretation === 'R' || (van.measuredValue && van.measuredValue <= 14));
  if (hasVreIndicator && isolate.organismName?.toLowerCase().includes('enterococcus')) {
    alerts.push({
      code: 'VRE',
      name: 'Vancomycin-Resistant Enterococcus (VRE)',
      category: 'Critical Infection Control Alert',
      severity: 'critical',
      evidence: `Vancomycin resistant (Zone <= 14mm / MIC >= 8). Likely vanA or vanB gene carrier.`,
      recommendedAction: 'Contact isolation precautions recommended; check Linezolid and Daptomycin susceptibility.'
    });
  }

  // Inducible Clindamycin Resistance (D-Test)
  const hasDTestIndicator =
    ery && ery.finalInterpretation === 'R' && cli && (cli.finalInterpretation === 'S' || cli.finalInterpretation === 'I');
  if (hasDTestIndicator) {
    alerts.push({
      code: 'ICR_DTEST',
      name: 'Inducible Clindamycin Resistance (erm-mediated / D-Zone)',
      category: 'Therapeutic Alert',
      severity: 'medium',
      evidence: `Erythromycin Resistant (R) + Clindamycin Susceptible (S). Potential inducible macrolide resistance.`,
      recommendedAction: 'Perform D-Zone disk approximation test before reporting Clindamycin as susceptible.'
    });
  }

  // Colistin Resistance (MCR)
  const hasColistinIndicator = col && (col.finalInterpretation === 'R' || (col.measuredValue && col.measuredValue > 2));
  if (hasColistinIndicator) {
    alerts.push({
      code: 'MCR_COL',
      name: 'Colistin Resistance / Pan-Drug Resistance (PDR) Warning',
      category: 'Emergency Antimicrobial Alert',
      severity: 'critical',
      evidence: `Colistin MIC elevated (> 2 µg/mL). Last-line antimicrobial failure risk.`,
      recommendedAction: 'Verify MIC by Broth Microdilution (BMD only - disk diffusion invalid); notify infectious diseases.'
    });
  }

  return alerts;
}

// 5. Smart Next-Action Engine
export function getSmartNextAction(data, activeTab) {
  if (!data) return { stage: 'LOADING', message: 'Loading culture case...', badge: 'Loading', targetTab: 'microscopy' };

  if (data.approved) {
    return {
      stage: 'RELEASED',
      title: 'Report Released & Approved',
      message: `Final report officially signed off and released on ${data.approvedAt ? new Date(data.approvedAt).toLocaleString() : 'today'}. Tamper-evident record frozen.`,
      badge: '✓ Final Released',
      badgeColor: '#15803d',
      buttonText: '🖨️ View / Print Official Report',
      actionType: 'PRINT_REPORT'
    };
  }

  // Check 1: Microscopy
  const hasMicroscopy = Boolean(data.gramStain || data.pusCells || data.microscopyObservations);
  if (!hasMicroscopy && activeTab !== 'microscopy') {
    return {
      stage: 'MICROSCOPY_PENDING',
      title: 'Direct Microscopy Pending',
      message: 'Initial Gram stain and wet mount observations have not been recorded yet. Complete microscopy to guide culture interpretation.',
      badge: 'Step 1: Microscopy',
      badgeColor: '#0284c7',
      buttonText: 'Proceed to Direct Microscopy',
      targetTab: 'microscopy'
    };
  }

  // Check 2: Culture Growth Status
  if (!data.cultureStatus || data.cultureStatus === 'Pending') {
    return {
      stage: 'CULTURE_INCUBATING',
      title: 'Culture In Incubation',
      message: 'Specimen is actively incubating. Record 24h / 48h plate readings and declare growth status when visible.',
      badge: 'Step 2: Culture Reading',
      badgeColor: '#d97706',
      buttonText: 'Record Culture Growth Status',
      targetTab: 'culture'
    };
  }

  // If Culture is No Growth
  if (data.cultureStatus === 'No Growth') {
    return {
      stage: 'NO_GROWTH_READY',
      title: 'No Growth Recorded — Ready for Report Sign-off',
      message: 'Culture yielded no growth after standard incubation. Insert standard No Growth remarks and proceed to sign-off.',
      badge: 'No Growth Finalization',
      badgeColor: '#059669',
      buttonText: 'Finalize & Sign-off Report',
      targetTab: 'validation'
    };
  }

  // Check 3: Organism Identification (if Growth Detected)
  if (data.cultureStatus === 'Growth' || data.cultureStatus === 'Mixed Growth') {
    if (!data.isolates || data.isolates.length === 0) {
      return {
        stage: 'ORGANISM_ID_REQUIRED',
        title: 'Significant Growth Detected — Organism Identification Required',
        message: 'Plate reading confirms bacterial growth. Add identified isolate(s) to automatically trigger AST panel configuration.',
        badge: 'Step 3: Add Isolate',
        badgeColor: '#dc2626',
        buttonText: '+ Add Identified Isolate',
        targetTab: 'ast',
        actionType: 'ADD_ISOLATE'
      };
    }

    // Check 4: AST Susceptibility Results
    const allAsts = data.isolates.flatMap((i) => i.astResults || []);
    const completedAsts = allAsts.filter((r) => r.measuredValue !== null && r.measuredValue !== undefined);
    if (allAsts.length === 0 || completedAsts.length === 0) {
      return {
        stage: 'AST_MEASUREMENT_PENDING',
        title: 'AST Measurements Required',
        message: `${data.isolates.length} isolate(s) identified. Enter raw disk diffusion (mm) or MIC (µg/mL) measurements for automated S/I/R interpretation.`,
        badge: 'Step 4: AST Entry',
        badgeColor: '#7c3aed',
        buttonText: 'Enter AST Susceptibility Values',
        targetTab: 'ast'
      };
    }

    // Check 5: Resistance & Expert Rules Review
    const resistanceAlerts = data.isolates.flatMap((i) => evaluateResistanceAlerts(i));
    if (resistanceAlerts.length > 0 && activeTab !== 'resistance') {
      return {
        stage: 'RESISTANCE_REVIEW_REQUIRED',
        title: 'Epidemiological Resistance Flag Detected',
        message: `${resistanceAlerts.length} resistance pattern(s) identified (e.g. ${resistanceAlerts[0].code}). Review epidemiological findings before final approval.`,
        badge: '⚠️ Resistance Review',
        badgeColor: '#dc2626',
        buttonText: 'Review Resistance Findings',
        targetTab: 'resistance'
      };
    }

    // Check 6: Comments & Interpretive Notes
    if (!data.finalRemarks && activeTab !== 'comments') {
      return {
        stage: 'COMMENTS_RECOMMENDED',
        title: 'Interpretive Clinical Comments',
        message: 'Technical results complete. Insert pre-configured clinical remarks or therapeutic guidance notes before release.',
        badge: 'Step 5: Remarks',
        badgeColor: '#0284c7',
        buttonText: 'Add Report Remarks',
        targetTab: 'comments'
      };
    }
  }

  // Default: Ready for Final Review
  return {
    stage: 'READY_FOR_REVIEW',
    title: 'Case Complete — Ready for Authorized Microbiologist Sign-off',
    message: 'All technical observations, isolates, AST interpretations, and remarks have been verified. Authorize and release final diagnostic report.',
    badge: '✓ Ready for Release',
    badgeColor: '#15803d',
    buttonText: 'Review & Release Report',
    targetTab: 'validation'
  };
}

// 6. Report Readiness Checklist Validator
export function checkReportReadiness(data) {
  const checklist = [];
  let isReadyForFinal = true;
  const blockingReasons = [];

  // 1. Patient & Accession
  checklist.push({
    id: 'patient_verified',
    label: 'Patient Identity & Lab Order Verified',
    status: Boolean(data?.patientName && data?.patientID && data?.specimenName) ? 'pass' : 'fail',
    detail: `${data?.patientName || 'Unknown'} (UHID: ${data?.patientID || '—'}) | Specimen: ${data?.specimenName || '—'}`
  });

  // 2. Microscopy
  const hasMicroscopy = Boolean(data?.gramStain || data?.pusCells || data?.microscopyObservations);
  checklist.push({
    id: 'microscopy_completed',
    label: 'Direct Microscopy Observations',
    status: hasMicroscopy ? 'pass' : 'warn',
    detail: data?.gramStain || data?.pusCells || 'Pending technical smear recording'
  });

  // 3. Culture Status
  const hasCulture = data?.cultureStatus && data?.cultureStatus !== 'Pending';
  if (!hasCulture) {
    isReadyForFinal = false;
    blockingReasons.push('Culture incubation is still marked as Pending.');
  }
  checklist.push({
    id: 'culture_status',
    label: 'Culture Incubation & Colony Count',
    status: hasCulture ? 'pass' : 'fail',
    detail: `${data?.cultureStatus || 'Pending'} — ${data?.colonyCount || 'No colony count'}`
  });

  // 4. Organism Identification & AST (if Growth)
  if (data?.cultureStatus === 'Growth' || data?.cultureStatus === 'Mixed Growth') {
    const hasIsolates = Boolean(data?.isolates && data.isolates.length > 0);
    if (!hasIsolates) {
      isReadyForFinal = false;
      blockingReasons.push('Culture marked as Growth but no organism isolates have been added.');
    }
    checklist.push({
      id: 'organism_id',
      label: 'Organism Identification',
      status: hasIsolates ? 'pass' : 'fail',
      detail: hasIsolates
        ? `${data.isolates.length} isolate(s): ${data.isolates.map((i) => i.organismName).join(', ')}`
        : 'Missing organism identification'
    });

    const allAst = (data?.isolates || []).flatMap((i) => i.astResults || []);
    const testedAst = allAst.filter((r) => r.measuredValue !== null && r.measuredValue !== undefined);
    const hasAst = testedAst.length > 0;
    if (!hasAst) {
      isReadyForFinal = false;
      blockingReasons.push('AST Susceptibility testing has no recorded measurements.');
    }
    checklist.push({
      id: 'ast_completed',
      label: 'AST Susceptibility Measurements',
      status: hasAst ? 'pass' : 'fail',
      detail: `${testedAst.length} of ${allAst.length} antimicrobial(s) measured & interpreted`
    });
  }

  // 5. Clinical Remarks
  checklist.push({
    id: 'remarks_verified',
    label: 'Microbiology Interpretive Remarks',
    status: Boolean(data?.finalRemarks) ? 'pass' : 'warn',
    detail: data?.finalRemarks ? 'Clinical remarks inserted' : 'No remarks recorded (optional but recommended)'
  });

  return {
    isReadyForFinal,
    checklist,
    blockingReasons
  };
}
